/**
 * ЭТАП 1 · Шаг 3 — изоморфное определение устройства для SSR-макета.
 *
 * Сервер: тип устройства из User-Agent запроса (getRequestHeader). Клиент:
 * из проброшенного сервером window.__SSR_DEVICE__ (чтобы первый клиентский
 * рендер СОВПАЛ с сервером — гидрация), затем useWindowSize по mount берёт
 * реальную ширину окна.
 *
 * createIsomorphicFn изолирует серверный импорт (@tanstack/react-start/server)
 * от клиентского бандла: .server-вариант и его импорты вырезаются на клиенте.
 * Парсер UA — ua-parser-js (телефоны определяет надёжно; планшеты/десктоп с
 * десктопным UA → 'desktop', что для mobile-vs-desktop макета корректно).
 */
import { createIsomorphicFn } from '@tanstack/react-start';
// используется ТОЛЬКО в .server() → вырезается из клиентского бандла
// @ts-ignore -- сабпат server-only
import { getRequestHeader } from '@tanstack/react-start/server';
import { UAParser } from 'ua-parser-js';

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

// Репрезентативные ширины: попадают в бакеты useWindowSize
// (isMobile <768, isTablet 768–1279, isDesktop >=1280).
export const DEVICE_WIDTH: Record<DeviceType, number> = {
  mobile: 375,
  tablet: 1000,
  desktop: 1440
};

export function deviceFromUA(ua?: string | null): DeviceType {
  if (!ua) {
    return 'desktop';
  }
  const type = new UAParser(ua).getDevice().type;
  if (type === 'mobile') {
    return 'mobile';
  }
  if (type === 'tablet') {
    return 'tablet';
  }
  return 'desktop';
}

export function deviceFromWidth(width: number): DeviceType {
  if (width < 768) {
    return 'mobile';
  }
  if (width < 1280) {
    return 'tablet';
  }
  return 'desktop';
}

export const getSSRDevice = createIsomorphicFn()
  .server((): DeviceType => {
    try {
      return deviceFromUA(getRequestHeader('user-agent'));
    } catch {
      return 'desktop';
    }
  })
  .client((): DeviceType => {
    const injected = (window as unknown as { __SSR_DEVICE__?: DeviceType })
      .__SSR_DEVICE__;
    return injected ?? deviceFromWidth(window.innerWidth);
  });

/** Ширина для стартового рендера (сервер+первый клиентский), byte-for-byte равная. */
export function getSSRWidth(): number {
  return DEVICE_WIDTH[getSSRDevice()];
}
